export const updateGrid = (data, loc, add, count = 1, fillValue = 0) => {
  let newdata = data.map((innerArray) => innerArray.slice());
  
  // Handle empty data case
  if (newdata.length === 0) {
    if (add) {
      // Create initial grid based on location
      if (loc === 'top' || loc === 'bottom') {
        // Adding rows to empty grid - create rows with single column
        for (let i = 0; i < count; i++) {
          newdata.push([fillValue]);
        }
      } else {
        // Adding columns to empty grid - create single row with columns
        newdata.push(Array(count).fill(fillValue));
      }
    }
    return newdata;
  }
  
  // Handle case where rows exist but columns are empty
  if (newdata[0].length === 0) {
    if (add && (loc === 'left' || loc === 'right')) {
      for (let i = 0; i < newdata.length; i++) {
        newdata[i] = Array(count).fill(fillValue);
      }
      return newdata;
    }
  }
  
  switch (loc) {
    case 'top':
      if (add) {
        for (let i = 0; i < count; i++) {
          newdata.unshift(Array(newdata[0].length).fill(fillValue));
        }
      } else {
        newdata.splice(0, Math.min(count, newdata.length));
      }
      break;
    case 'bottom':
      if (add) {
        for (let i = 0; i < count; i++) {
          newdata.push(Array(newdata[0].length).fill(fillValue));
        }
      } else {
        newdata.splice(Math.max(0, newdata.length - count), count);
      }
      break;
    case 'left':
      for (let i = 0; i < newdata.length; i++) {
        if (add) {
          // Add columns to the left
          newdata[i] = Array(count).fill(fillValue).concat(newdata[i]);
        } else {
          // Remove columns from the left
          const removeCount = Math.min(count, newdata[i].length);
          newdata[i] = newdata[i].slice(removeCount);
        }
      }
      break;
    case 'right':
      for (let i = 0; i < newdata.length; i++) {
        if (add) {
          newdata[i] = newdata[i].concat(Array(count).fill(fillValue));
        } else {
          newdata[i] = newdata[i].slice(0, Math.max(0, newdata[i].length - count));
        }
      }
      break;
    default:
      return data;
  }
  
  // Ensure we never return empty grid
  if (newdata.length === 0) {
    newdata = [[fillValue]];
  } else if (newdata.length > 0 && newdata[0].length === 0) {
    // If we have rows but no columns, create at least one column
    for (let i = 0; i < newdata.length; i++) {
      newdata[i] = [fillValue];
    }
  }
  
  return newdata;
};

// Selection utilities
export const normalizeSelection = (selection) => {
  if (!selection) return null;
  
  const { startRow, startCol, endRow, endCol } = selection;
  return {
    minRow: Math.min(startRow, endRow),
    maxRow: Math.max(startRow, endRow),
    minCol: Math.min(startCol, endCol),
    maxCol: Math.max(startCol, endCol)
  };
};

export const getSelectionDimensions = (selection) => {
  const normalized = normalizeSelection(selection);
  if (!normalized) return { height: 0, width: 0 };
  
  return {
    height: normalized.maxRow - normalized.minRow + 1,
    width: normalized.maxCol - normalized.minCol + 1
  };
};

export const extractSelectionData = (data, selection) => {
  const normalized = normalizeSelection(selection);
  if (!normalized) return [];
  
  const { minRow, maxRow, minCol, maxCol } = normalized;
  const selectedData = [];
  
  for (let i = minRow; i <= maxRow; i++) {
    selectedData.push(data[i].slice(minCol, maxCol + 1));
  }
  
  return selectedData;
};

export const applySelectionData = (originalData, selectionData, selection) => {
  const normalized = normalizeSelection(selection);
  if (!normalized || !selectionData.length) return originalData;
  
  const { minRow, minCol } = normalized;
  const newData = [...originalData];
  
  for (let i = 0; i < selectionData.length; i++) {
    for (let j = 0; j < selectionData[i].length; j++) {
      if (newData[minRow + i] && newData[minRow + i][minCol + j] !== undefined) {
        newData[minRow + i][minCol + j] = selectionData[i][j];
      }
    }
  }
  
  return newData;
};

// Data transformation utilities
export const flipDataHorizontally = (data) => {
  return data.map(row => [...row].reverse());
};

export const flipDataVertically = (data) => {
  return [...data].reverse();
};

export const rotateDataClockwise = (data) => {
  if (!data.length || !data[0].length) return data;
  
  const rows = data.length;
  const cols = data[0].length;
  const rotated = [];
  
  // Rotate 90 degrees clockwise: new[j][height-1-i] = original[i][j]
  for (let j = 0; j < cols; j++) {
    rotated[j] = [];
    for (let i = rows - 1; i >= 0; i--) {
      rotated[j][rows - 1 - i] = data[i][j];
    }
  }
  
  return rotated;
};

export const rotateDataCounterClockwise = (data) => {
  if (!data.length || !data[0].length) return data;
  
  const rows = data.length;
  const cols = data[0].length;
  const rotated = [];
  
  // Rotate 90 degrees counter-clockwise: new[width-1-j][i] = original[i][j]
  for (let j = cols - 1; j >= 0; j--) {
    rotated[cols - 1 - j] = [];
    for (let i = 0; i < rows; i++) {
      rotated[cols - 1 - j][i] = data[i][j];
    }
  }
  
  return rotated;
};

// Bounds checking utilities
export const canRotateSelection = (selection, chartRows, chartCols) => {
  const dimensions = getSelectionDimensions(selection);
  const normalized = normalizeSelection(selection);
  
  if (!normalized || !dimensions) return false;
  
  const { minRow, minCol } = normalized;
  const { height, width } = dimensions;
  
  // After rotation, width becomes height and height becomes width
  return (minRow + width <= chartRows) && (minCol + height <= chartCols);
};

// Replace selection data in the main data array with new data
export const replaceSelectionData = (data, newData, selection) => {
  const normalized = normalizeSelection(selection);
  return data.map((row, rowIndex) =>
    row.map((cell, colIndex) => {
      if (rowIndex >= normalized.minRow && rowIndex <= normalized.maxRow &&
          colIndex >= normalized.minCol && colIndex <= normalized.maxCol) {
        const localRow = rowIndex - normalized.minRow;
        const localCol = colIndex - normalized.minCol;
        if (localRow < newData.length && localCol < (newData[localRow]?.length || 0)) {
          return newData[localRow][localCol];
        }
      }
      return cell;
    })
  );
};

// Fill utility - flood fill algorithm for adjacent cells of the same color
export const floodFill = (data, startRow, startCol, newColor) => {
  if (!data || !data[startRow] || data[startRow][startCol] === undefined) {
    return data;
  }

  const rows = data.length;
  const cols = data[0].length;
  const originalColor = data[startRow][startCol];
  
  // If the new color is the same as the original, no change needed
  if (originalColor === newColor) {
    return data;
  }

  const newData = data.map(row => [...row]);
  const stack = [[startRow, startCol]];
  const visited = new Set();

  while (stack.length > 0) {
    const [row, col] = stack.pop();
    const key = `${row},${col}`;
    
    // Skip if already visited or out of bounds
    if (visited.has(key) || row < 0 || row >= rows || col < 0 || col >= cols) {
      continue;
    }
    
    // Skip if not the same color as original
    if (newData[row][col] !== originalColor) {
      continue;
    }
    
    // Mark as visited and fill
    visited.add(key);
    newData[row][col] = newColor;
    
    // Add adjacent cells to stack (4-directional)
    stack.push([row - 1, col]); // up
    stack.push([row + 1, col]); // down
    stack.push([row, col - 1]); // left
    stack.push([row, col + 1]); // right
  }

  return newData;
};
